import type {
	StoragePipelineOptions,
	BlockBlobParallelUploadOptions,
	RestError,
} from '@azure/storage-blob';
import { t } from '@transifex/native';

import { MAX_FILE_SIZE } from 'pkg/config/files';

import * as endpoints from 'pkg/api/endpoints/auto';
import type * as models from 'pkg/api/models';
import { create as modelsCreate } from 'pkg/api/models/actions';
import { AttachmentType, AttachmentStatus } from 'pkg/api/models/attachment';
import type { AttachmentVisibility } from 'pkg/api/models/attachment';
import { typeOfFile, VideoSizeTimeout } from 'pkg/api/models/attachment/file';
import * as sdk from 'pkg/core/sdk';
import { convertHeicToJpeg, heicFileNameToJpeg } from 'pkg/heic';
import { delay } from 'pkg/timings';

type AttachmentCompleteCallback = (
	attachment: models.attachment.Attachment
) => void;

type AttachmentProgressCallback = (
	bytesLoaded: number,
	bytesTotal: number,
	attachment: models.attachment.Attachment
) => void;

interface AttachmentUploadOptions {
	waitForCompleteEvent?: boolean;
	onComplete?: AttachmentCompleteCallback;
	onProgress?: AttachmentProgressCallback;
	/** The attachment row exists; the bytes are about to follow. */
	onCreated?: (attachment: models.attachment.Attachment) => void;
	/** Stops the blob upload. The attachment row stays for the caller to delete. */
	abortSignal?: AbortSignal;
}

export interface AttachmentCreatePayload {
	tmpId?: string;
	file: File;
	title?: string;
	description?: string;
	type?: AttachmentType;
	visibility: AttachmentVisibility;
	url?: string;
	size?: number;
	originalFilename?: string;
	duration?: number;
	peaks?: number[];
	/** A video's size, which the API can't read itself. See `videoSizeOf`. */
	width?: number;
	height?: number;
}

const AttachmentBytesBlockSize = 4 * 1024 * 1024;

interface MediaSize {
	width: number;
	height: number;
}

/**
 *	A video's size as it plays, upright, from its header. The API decodes an
 *	image to store its size but can't decode a video, so the client measures
 *	it, and a chat reserves the clip's box from it before it loads. Nothing
 *	when this browser can't read the clip or doesn't say in time.
 */
export function videoSizeOf(file: File): Promise<MediaSize | undefined> {
	const video = document.createElement('video');
	const url = URL.createObjectURL(file);

	return new Promise<MediaSize | undefined>((resolve) => {
		const timer = setTimeout(() => resolve(undefined), VideoSizeTimeout);

		video.onloadedmetadata = () => {
			clearTimeout(timer);

			const { videoWidth: width, videoHeight: height } = video;

			resolve(width > 0 && height > 0 ? { width, height } : undefined);
		};

		video.onerror = () => {
			clearTimeout(timer);
			resolve(undefined);
		};

		video.muted = true;
		video.preload = 'metadata';
		video.src = url;
	}).finally(() => {
		// Lets go of the decoder as well as the file.
		video.removeAttribute('src');
		video.load();
		URL.revokeObjectURL(url);
	});
}

export async function upload(
	payload: AttachmentCreatePayload,
	{ onProgress, onCreated, abortSignal }: AttachmentUploadOptions = {}
): Promise<models.attachment.Attachment> {
	// iOS uploads raw HEIC camera files, which nothing outside WebKit can
	// display. Re-encode to JPEG here, where the browser can still decode
	// them; a no-op for everything else.
	const pickedFile = payload.file;
	payload.file = await convertHeicToJpeg(pickedFile);
	if (payload.file !== pickedFile) {
		// Callers may have named the attachment after the picked file.
		if (payload.title) {
			payload.title = heicFileNameToJpeg(payload.title);
		}
		if (payload.originalFilename) {
			payload.originalFilename = heicFileNameToJpeg(payload.originalFilename);
		}
	}

	// A voice note is audio too, but says so itself.
	if (payload.type !== AttachmentType.Voice) {
		payload.type = typeOfFile(payload.file);
	}

	if (!payload.originalFilename) {
		payload.originalFilename = payload.file.name;
	}

	payload.size = payload.file.size;
	if (!payload.title) {
		payload.title = payload.originalFilename;
	}

	if (payload.file.size > MAX_FILE_SIZE) {
		alert(t(`The max file size is 25MB.`));
		return null;
	}

	if (payload.type === AttachmentType.Video) {
		Object.assign(payload, await videoSizeOf(payload.file));
	}

	const [request, attachment] = await modelsCreate<
		AttachmentCreatePayload,
		models.attachment.Attachment
	>(endpoints.Attachments.Create(), payload);

	if (!request.ok) {
		throw new Error('failed to create attachment');
	}

	onCreated?.(attachment);

	return uploadToBlobAndComplete(attachment, payload.file, {
		onProgress,
		abortSignal,
	});
}

// Uploads the file bytes to Azure via the attachment's SAS uploadUrl, then
// finalizes via the complete-upload endpoint.
export async function uploadToBlobAndComplete(
	attachment: models.attachment.Attachment,
	file: File,
	{ onProgress, abortSignal }: AttachmentUploadOptions = {}
): Promise<models.attachment.Attachment> {
	const handleComplete = async (): Promise<models.attachment.Attachment> => {
		const request = await sdk.post(
			endpoints.Attachments.CompleteUpload(attachment.id)
		);

		if (request.ok) {
			const response = await request.json();
			return response;
		}
	};

	const handleProgress = (progress: number) => {
		if (onProgress) {
			onProgress(progress, attachment.size, attachment);
		}
	};

	if (attachment.status === AttachmentStatus.Uploading) {
		// 1.3 MB of SDK that only an upload needs; a static import put it on the
		// boot chunk for every session that never attaches a file.
		const { BlockBlobClient, newPipeline, AnonymousCredential } =
			await import('@azure/storage-blob');

		const blob = new BlockBlobClient(
			attachment.uploadUrl,
			newPipeline(new AnonymousCredential(), {
				retryOptions: 4,
			} as StoragePipelineOptions)
		);

		const uploadOptions = {
			abortSignal,
			blockSize: AttachmentBytesBlockSize,
			parallelism: 20,
			blobHTTPHeaders: {
				blobContentType: file.type,
				blobContentDisposition: 'inline',
			},
		} as BlockBlobParallelUploadOptions;

		return new Promise((resolve, reject) => {
			let hasStartedCompleting = false;

			const completeOnce = async () => {
				if (hasStartedCompleting) {
					return;
				}

				hasStartedCompleting = true;

				try {
					await delay(1000);
					const att = await handleComplete();
					if (!att) {
						reject(new Error('Failed to complete attachment upload'));
						return;
					}
					resolve(att);
				} catch (error) {
					reject(error);
				}
			};

			uploadOptions.onProgress = async (progress: { loadedBytes: number }) => {
				handleProgress(progress.loadedBytes);
				if (progress.loadedBytes >= attachment.size) {
					await completeOnce();
				}
			};

			blob.uploadData(file, uploadOptions).catch((error: RestError) => {
				// A retry after a lost response finds the blob already stored; completing checks its size.
				// Azure documents UnauthorizedBlobOverwrite; Azurite sends AuthorizationPermissionMismatch.
				if (
					error.code === 'UnauthorizedBlobOverwrite' ||
					error.code === 'AuthorizationPermissionMismatch'
				) {
					void completeOnce();
					return;
				}

				reject(new Error(`Failed to upload attachment: ${error.message}`));
			});
		});
	}

	return attachment;
}
